import type * as lazyIndex from "./lazyIndex";
import { LazyModule, registerLazyOrchestrator, LazyAction } from "owa-bundling";
import { onAppBarTrigger, onOpenAboutDialog, onUninstallMetaOsApp, onFlyoutButtonHover, onSaveDevicePermissions, onCloseDevicePermissions, onOpenDevicePermissionsDialog, } from "owa-nova-actions";
import { onModuleClick } from "owa-workloads";
import onRefreshAppBarData from "owa-appbar-state/lib/actions/onRefreshAppBarData";
import { lazyInitializeAppBarPinnedAppsUserSetting } from "owa-appbar-pinnedapps";
import { lazyGovern, GovernPriority } from "owa-tti";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import { isAppBarEnabled } from "./utils/isAppBarEnabled";

//import for orchestrators used in Appbar ContextMenu/SecondaryActions for Monarch (eg: 'onPinShortcutOrchestrator')
/* eslint-disable-next-line owa-custom-rules/forbid-empty-import-statements-from-modules -- (https://aka.ms/OWALintWiki)
 * Baseline. DO NOT COPY AND PASTE!, replace this line with a justification or fix the issue.
 *	> Empty import statements from non-relative modules are not allowed. Either import something specific or remove the import. */
import "native-controls";

// Import for necessary non-lazy orchestrators used in App Bar DND.
// In particular, we need the orchestrator for onContentDropped to be non-lazy, since the DataTransfer object carried in its
// payload needs to be read before the intrinsic drag store is cleared/nonreadable. Due to the structure
// of the object typings and Nova event, this is the first available opportunity we have to read this payload.
/* eslint-disable-next-line owa-custom-rules/forbid-empty-import-statements-from-modules -- (https://aka.ms/OWALintWiki)
 * Baseline. DO NOT COPY AND PASTE!, replace this line with a justification or fix the issue.
 *	> Empty import statements from non-relative modules are not allowed. Either import something specific or remove the import. */
import "owa-appbar-dnd";

import "./orchestrators/onAcquisitionsRemoteRefreshOrchestrator";

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule(() => import(/* webpackChunkName: "AppBar" */ "./lazyIndex"), {
    name: "AppBar",
});

registerLazyOrchestrator(onAppBarTrigger, lazyModule, m => m.appBarEventOrchestrator);
registerLazyOrchestrator(onModuleClick, lazyModule, m => m.onModuleClickOrchestrator);
registerLazyOrchestrator(onOpenAboutDialog, lazyModule, m => m.onOpenAboutDialogOrchestrator);
registerLazyOrchestrator(onRefreshAppBarData, lazyModule, m => m.onRefreshAppBarDataOrchestrator);
registerLazyOrchestrator(onUninstallMetaOsApp, lazyModule, m => m.onUninstallMetaOsAppOrchestrator);
registerLazyOrchestrator(onFlyoutButtonHover, lazyModule, m => m.onFlyoutButtonHoverOrchestrator);
registerLazyOrchestrator(onSaveDevicePermissions, lazyModule, m => m.onSaveDevicePermissionsOrchestrator);
registerLazyOrchestrator(onCloseDevicePermissions, lazyModule, m => m.onCloseDevicePermissionsOrchestrator);
registerLazyOrchestrator(onOpenDevicePermissionsDialog, lazyModule, m => m.onOpenDevicePermissionsDialogOrchestrator);
const lazyInitializeAcquisitionsData: LazyAction<typeof lazyIndex.initializeAcquisitionsData, typeof lazyModule> = new LazyAction(lazyModule, m => m.initializeAcquisitionsData);
export { default as AppBar } from "./components/AppBar";

// Run remote apps initialization if user has not hovered on flyout yet
/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function importAndExecute called at module scope is not allowed. This may cause unwanted side effects. */
lazyGovern.importAndExecute({
    task: async () => {
        // condition is included as part of task, since otherwise it will be evaluated before underlying data (e.g. app module store) is initialized
        // and return the wrong result
        if (isAppBarEnabled()) {
            const mailboxInfo = getModuleContextMailboxInfo();
            await lazyInitializeAcquisitionsData.importAndExecute(mailboxInfo);
            return lazyInitializeAppBarPinnedAppsUserSetting.importAndExecute(mailboxInfo);
        }
    },
    priority: GovernPriority.Idle,
    name: "initializeAppBar",
    // Generated by Copilot. Please update with the correct owners.
    ownership: ["jobutle@microsoft.com"],
});

export { isAppBarEnabled } from "./utils/isAppBarEnabled";
